import React from "react";
import DateTimeField from "../field/DateTimeField";
import { Field } from "react-final-form";
import { format as formatFn, parse as parseFn, getHours, getMinutes } from "date-fns";
import { localize, LocaleContext, dateToInstantISOString } from "cci-utility";
import { injectIntl, defineMessages } from "react-intl";

const messages = defineMessages({
  invalidDateTime: {
    id: "invalidDateTime",
    defaultMessage: "Invalid"
  }
});

/*
 * This components allows to select date and time to combine them in one value.
 * Since we use Instant in sever for date time, this component receives and return
 * date time in format 2011-12-03T10:15:30Z.
 */
class FormDateTimeField extends React.Component {
  constructor() {
    super();
    this.state = { dateValue: "", timeValue: "" };
    this.handleBlur = this.handleBlur.bind(this);
    this.mergeDateTime = this.mergeDateTime.bind(this);
  }

  handleBlur(dateParseFormat, timeParseFormat, locale, e) {
    const { dateValue, timeValue } = this.state,
      { onBlur, onInvalidBlur } = this.props,
      newDateTime = this.mergeDateTime({
        dateText: dateValue,
        dateParseFormat,
        timeText: timeValue,
        timeParseFormat,
        locale
      });

    if (newDateTime.valid) {
      onBlur({...e, text: newDateTime});
    } else {
      onInvalidBlur();
    }
  }

  /*
   * Merge date and time in one datetime string. Returns an object with the following props:
   *  - valid: True when this is a valid date or empty date, false otherwise.
   *  - value: The string datetime value in with 2011-12-03T10:15:30Z when valid, or undefined when not valid.
   */
  mergeDateTime({ dateText, dateParseFormat, timeText, timeParseFormat, locale }) {
    if (dateText === "" && timeText === "") {
      // Empty value
      return { valid: true, value: "" };
    }

    let date = parseFn(dateText, dateParseFormat, new Date(), { locale });
    let time = parseFn(timeText, timeParseFormat, new Date(), { locale });

    if (isNaN(date) || isNaN(time)) {
      // One of the values is not valid
      return { valid: false };
    }

    date.setHours(getHours(time));
    date.setMinutes(getMinutes(time));

    // Valid value
    return { valid: true, value: dateToInstantISOString(date) };
  }

  render() {
    const { onBlur, onInvalidBlur, onChange, onInvalidChange, intl, ...restProps } = this.props,
      parseFormat = "yyyy-MM-dd'T'HH:mm:ssXX",
      dateParseFormat = "yyyy-MM-dd",
      timeParseFormat = "HH:mm",
      INVALID_VALUE = "INVALID_DATETIME";

    return (
      <LocaleContext.Consumer>
        {({date: locale}) => (
          <Field
            name={restProps.name}
            // We do this Field-Level validation to check that date and time fields are valid
            validate={value => {
              if (value) {
                const dateTimeValue = parseFn(value, parseFormat, new Date(), { locale });

                if (!isNaN(dateTimeValue)) {
                  // Valid datetime
                  return undefined;
                }
                // Invalid datetime
                return localize(intl, messages.invalidDateTime);
              }
              // No value specified so there is no error
              return undefined;
            }}
          >
            {({ input, meta }) => {
              // Date and time fields value are taken from state unless there is a valid datetime in form state
              let { dateValue, timeValue } = this.state;
              const { name, value } = input,
                dateTimeValue = parseFn(value, parseFormat, new Date(), { locale });

              if (!value) {
                dateValue = "";
                timeValue = "";
              } else if (!isNaN(dateTimeValue)) {
                dateValue = formatFn(dateTimeValue, "yyyy-MM-dd", { locale });
                timeValue = formatFn(dateTimeValue, "HH:mm", { locale });
              }

              return (
                <DateTimeField
                  {...restProps}
                  dateValue={dateValue}
                  timeValue={timeValue}
                  dateParseFormat={dateParseFormat}
                  timeParseFormat={timeParseFormat}
                  error={meta.error || meta.submitError}
                  onTimeChange={(date, text) => {
                    // Always update the state in order to update the field value
                    this.setState({ dateValue: date, timeValue: text });

                    const newDateTime = this.mergeDateTime({
                      dateText: date,
                      dateParseFormat,
                      timeText: text,
                      timeParseFormat,
                      locale
                    });

                    if (newDateTime.valid) {
                      input.onChange(newDateTime.value);
                      onChange(newDateTime.value);
                    } else {
                      // We set an invalid date in the form state to trigger the validation error
                      input.onChange(INVALID_VALUE);
                      onInvalidChange();
                    }
                  }}
                  onDateChange={(text, time) => {
                    // Always update the state in order to update the field value
                    this.setState({ dateValue: text, timeValue: time });

                    const newDateTime = this.mergeDateTime({
                      dateText: text,
                      dateParseFormat,
                      timeText: time,
                      timeParseFormat,
                      locale
                    });

                    if (newDateTime.valid) {
                      input.onChange(newDateTime.value);
                      onChange(newDateTime.value);
                    } else {
                      // We set an invalid date in the form state to trigger the validation error
                      input.onChange(INVALID_VALUE);
                      onInvalidChange();
                    }
                  }}
                  onTimeBlur={e => {
                    input.onBlur();
                    this.handleBlur(dateParseFormat, timeParseFormat, locale, e);
                  }}
                  onDateBlur={e => {
                    input.onBlur();
                    this.handleBlur(dateParseFormat, timeParseFormat, locale, e);
                  }}
                />
              );
            }}
          </Field>
        )}
      </LocaleContext.Consumer>
    );
  }
}

FormDateTimeField.defaultProps = {
  onChange: () => {},
  onInvalidChange: () => {},
  onBlur: () => {},
  onInvalidBlur: () => {}
};

export default injectIntl(FormDateTimeField);
